<template>
  <div
    class="maintenance-card bg-white rounded-lg shadow-md p-4 border-l-4"
    :class="maintenanceTypeBorderClass"
  >
    <div class="flex justify-between items-start mb-3">
      <div>
        <h3 class="text-lg font-semibold text-gray-800">{{ maintenance.description }}</h3>
        <p class="text-sm text-gray-600" v-if="maintenance.equipment">
          For: {{ maintenance.equipment.name }}
        </p>
      </div>
      <span class="px-2 py-1 text-xs rounded-full" :class="maintenanceTypeBadgeClass">
        {{ maintenance.maintenance_type }}
      </span>
    </div>

    <div class="space-y-2 text-sm text-gray-600">
      <div class="flex justify-between">
        <span>Date:</span>
        <span class="font-medium">{{ formatDate(maintenance.maintenance_date) }}</span>
      </div>

      <div class="flex justify-between" v-if="maintenance.next_due_date">
        <span>Next Due:</span>
        <span class="font-medium">{{ formatDate(maintenance.next_due_date) }}</span>
      </div>

      <div class="flex justify-between" v-if="maintenance.cost">
        <span>Cost:</span>
        <span class="font-medium">TSH {{ Number(maintenance.cost).toFixed(2) }}</span>
      </div>

      <div class="flex justify-between" v-if="maintenance.technician">
        <span>Technician:</span>
        <span class="font-medium">{{ maintenance.technician }}</span>
      </div>

      <div v-if="maintenance.notes" class="mt-2">
        <p class="text-sm text-gray-600 italic">"{{ maintenance.notes }}"</p>
      </div>
    </div>

    <div class="mt-4 flex space-x-2">
      <button @click="$emit('edit', maintenance)" class="btn-secondary text-xs px-3 py-1">
        Edit
      </button>
      <button @click="$emit('view', maintenance)" class="btn-primary text-xs px-3 py-1">
        View
      </button>
      <button @click="$emit('delete', maintenance.id)" class="btn-danger text-xs px-3 py-1">
        Delete
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Maintenance } from '@/types/maintenance'

interface Props {
  maintenance: Maintenance
}

const props = defineProps<Props>()
defineEmits<{
  (e: 'edit', maintenance: Maintenance): void
  (e: 'view', maintenance: Maintenance): void
  (e: 'delete', id: number): void
}>()

const maintenanceTypeBorderClass = computed(() => {
  switch (props.maintenance.maintenance_type) {
    case 'scheduled':
      return 'border-blue-500'
    case 'repair':
      return 'border-red-500'
    case 'inspection':
      return 'border-green-500'
    default:
      return 'border-gray-300'
  }
})

const maintenanceTypeBadgeClass = computed(() => {
  switch (props.maintenance.maintenance_type) {
    case 'scheduled':
      return 'bg-blue-100 text-blue-800'
    case 'repair':
      return 'bg-red-100 text-red-800'
    case 'inspection':
      return 'bg-green-100 text-green-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
})

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString()
}
</script>

<style scoped>
.maintenance-card {
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.maintenance-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
</style>
